import { useState, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as outbox from './lib/outbox.js';

export function useOutbox() {
  return useSyncExternalStore(outbox.subscribe, outbox.getSnapshot);
}

const icon = (d) => (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    {d}
  </svg>
);
export const PencilIcon = icon(<><path d="m4 20 1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19z" /><path d="m14.5 6.5 3 3" /></>);
export const BoxIcon = icon(<><path d="m4 8 8-4 8 4v9l-8 4-8-4z" /><path d="m4 8 8 4 8-4M12 12v9" /></>);
export const NoteIcon = icon(<><path d="M5 3h10l4 4v14H5z" /><path d="M14 3v5h5M8 12h8M8 16h6" /></>);
export const CameraIcon = icon(<><path d="M4 8h3l2-3h6l2 3h3v12H4z" /><circle cx="12" cy="13.5" r="3.5" /></>);
export const PersonIcon = icon(<><circle cx="12" cy="8" r="4" /><path d="M4 21c1-4.5 4-6 8-6s7 1.5 8 6" /></>);
export const SearchIcon = icon(<><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>);
export const CheckIcon = icon(<path d="m5 12.5 4.5 4.5L19 7.5" />);
export const BackIcon = icon(<path d="m15 5-7 7 7 7" />);
export const ImageIcon = icon(<><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="1.5" /><path d="m21 16-5-5-9 9" /></>);
export const VideoIcon = icon(<><rect x="3" y="6" width="13" height="12" rx="2" /><path d="m16 11 5-3v8l-5-3z" /></>);

export function Spinner({ className = 'h-6 w-6' }) {
  return <div className={`${className} animate-spin rounded-full border-2 border-steel border-t-transparent`} role="status" aria-label="Loading" />;
}

export function JobCard({ job, to, staff }) {
  return (
    <Link to={to} className="card block p-4 active:scale-[0.99] transition">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-lg font-bold leading-snug">{job.name}</div>
          <div className="mt-0.5 text-sm text-steel">
            {[job.account, job.siteAddress].filter(Boolean).join(' · ') || ' '}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {job.jobNumber && <span className="badge bg-amber text-charcoal">{job.jobNumber}</span>}
          {staff && job.stage && <span className="badge bg-steel/20 text-steel">{job.stage}</span>}
        </div>
      </div>
    </Link>
  );
}

export function ErrorBox({ children }) {
  return <div className="rounded-xl border border-ascred/40 bg-ascred/10 p-4 text-ascred dark:text-red-300" role="alert">{children}</div>;
}

// Persistent "things are still uploading" banner, shown on every screen.
export function OutboxBanner() {
  const { items, authRequired } = useOutbox();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  if (!items.length) return null;

  const failed = items.filter((i) => i.state === 'failed');
  const files = items.filter((i) => i.kind === 'file');
  const active = items.find((i) => i.state === 'uploading');
  const headline = authRequired
    ? 'Please sign in again to finish sending'
    : failed.length
      ? `${failed.length} item${failed.length === 1 ? '' : 's'} couldn't be sent`
      : `Sending ${items.length} item${items.length === 1 ? '' : 's'} – keep this screen open`;

  return (
    <div className="border-b border-amber/60 bg-amber/20 px-4 py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold">{headline}</div>
          {active?.kind === 'file' && !authRequired && (
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-charcoal/15 dark:bg-white/20">
              <div className="h-full bg-navy dark:bg-amber" style={{ width: `${Math.round((active.progress || 0) * 100)}%` }} />
            </div>
          )}
        </div>
        {authRequired ? (
          <a className="btn-primary !px-3 !py-2 !text-sm" href={`/auth/login?returnTo=${encodeURIComponent(loc.pathname)}`}>Sign in</a>
        ) : (
          <button className="shrink-0 underline" onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Details'}</button>
        )}
      </div>
      {open && (
        <ul className="mt-3 space-y-2">
          {items.filter((i) => i.kind !== 'complete' && !i.quiet).map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-2 rounded-lg bg-white/60 px-3 py-2 dark:bg-charcoal/60">
              <div className="min-w-0">
                <div className="truncate font-medium">{i.kind === 'file' ? i.name : i.kind === 'goods' ? 'Goods note' : 'Note'} <span className="text-steel">· {i.jobName}</span></div>
                <div className="text-xs text-steel">
                  {i.state === 'uploading' ? 'Sending…' : i.state === 'failed' ? i.error : i.error || 'Waiting'}
                </div>
              </div>
              {i.state === 'failed' && (
                <div className="flex shrink-0 gap-2">
                  <button className="underline" onClick={() => outbox.retry(i.id)}>Retry</button>
                  <button className="text-ascred underline" onClick={() => outbox.discard(i.id)}>Discard</button>
                </div>
              )}
            </li>
          ))}
          {files.length === 0 && items.every((i) => i.kind === 'complete' || i.kind === 'goods') && (
            <li className="text-xs text-steel">Posting to the job…</li>
          )}
        </ul>
      )}
    </div>
  );
}

// Wraps a per-job page: loading / not found / error handling + the job title bar.
import { useParams } from 'react-router-dom';
import { useJob } from './lib/useJob.js';

export function JobPage({ title, children, backTo }) {
  const { id } = useParams();
  const { status, job } = useJob(id);
  if (status === 'loading') return <div className="flex justify-center py-16"><Spinner className="h-10 w-10" /></div>;
  if (status !== 'ok') {
    return (
      <div className="space-y-4">
        <ErrorBox>{status === 'missing' ? "That job isn't available." : "Couldn't load the job. Check your signal and try again."}</ErrorBox>
        <Link to="/" className="btn-ghost w-full">Choose a job</Link>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <Link to={backTo === 'search' ? '/' : `/job/${job.id}`} className="inline-flex items-center gap-1 py-1 text-steel">
        <BackIcon className="h-5 w-5" /> {backTo === 'search' ? 'All jobs' : job.name}
      </Link>
      {title && (
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <p className="text-steel">{job.name}{job.jobNumber ? ` · ${job.jobNumber}` : ''}</p>
        </div>
      )}
      {typeof children === 'function' ? children(job) : children}
    </div>
  );
}
